import { useQuery } from '@tanstack/react-query';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';
import { normalizePaymentRows } from '~shared/helpers/normalize-payments';

export interface GetPaymentTransactionListResponse {
  data: Array<PaymentTransaction>;
  total: number;
  page: number;
  limit: number;
}

export interface PaymentTransaction {
  classChargeName: string;
  discountAdjustment: number;
  invoiceDate: Date;
  invoiceId: string;
  invoiceValue: number;
  lineItems: Array<LineItem>;
  paidAmount: number;
  paidDate: Date | null;
  parentId: string;
  parentName: string;
  paymentId: string;
  payments: Array<Payment>;
  refundAmount: number;
  refundId: null;
  status: string;
  studentId: string;
  studentName: string;
  transactionCode: null;
  /** When Stripe will make funds available (for paid transactions). */
  fundsAvailableOn: string | null;
}

export interface LineItem {
  amount: number | string;
  name: string;
  quantity: number;
  total: number | string;
  type: string;
}

export interface Payment {
  amount: number;
  date: string;
  method: string;
  paymentIntentId: string;
}

export interface GetPaymentTransactionListParams {
  page: number; // Current page number
  limit: number; // Limit of items per page
  startDate?: string; // Optional filter by invoice date range (YYYY-MM-DD)
  endDate?: string; // Optional filter by invoice date range (YYYY-MM-DD)
  parentName?: string; // Optional filter by parent name
  studentName?: string; // Optional filter by student name
  classChargeName?: string; // Optional filter by class charge name
  transactionCode?: string; // Optional filter by transaction code
  status?: string; // Optional filter by transaction status
}

export const getPaymentTransactionList = async (
  params: GetPaymentTransactionListParams,
) => {
  const response = await baseAPI.get<GetPaymentTransactionListResponse>(
    'payment-transaction/list',
    {
      params,
    },
  );
  if (!response.data) {
    return {
      data: [],
      total: 0,
      page: params.page,
      limit: params.limit,
    };
  }

  return {
    ...response.data,
    data: normalizePaymentRows(response.data.data),
  };
};

export const useGetPaymentTransactionList = (
  params: GetPaymentTransactionListParams,
) => {
  return useQuery({
    queryKey: [KEYS.PAYMENT_TRANSACTION_LIST, params],
    queryFn: () => getPaymentTransactionList(params),
    // staleTime: 1000 * 60 * 5, // 5 minutes
  });
};
